// What the shell shows where, as data: the nav destinations (four or fewer tabs on a phone, the
// rest under More) and which chrome each screen keeps. Focus mode keeps none of it: only the card,
// its place in the round and the key hints.
//
// The sidebar stays short ("Quiet triage"): five places you go every day, then a small muted
// "Put away" group. What explains or steers Claude lives in the Claude menu of the top bar;
// Accounts lives in Settings. A phone lists all of it under More.
import type { ComponentType } from "react";
import {
  Bookmark,
  BookOpen,
  EyeOff,
  FilePen,
  History,
  Inbox,
  Layers,
  ListChecks,
  ListTodo,
  Mails,
  MessageCircleQuestion,
  Newspaper,
  Settings,
  SlidersHorizontal,
  Sparkles,
  UserCheck,
} from "lucide-react";

type Icon = ComponentType<{ className?: string }>;

export interface NavItem {
  to:
    | "/feed"
    | "/inbox"
    | "/tasks"
    | "/saved"
    | "/hidden"
    | "/recent"
    | "/screener"
    | "/accounts"
    | "/settings";
  label: string;
  icon: Icon;
  binding: string;
  /** Other paths that count as this item (thread pages are "Inbox", focus is "Feed"). */
  alsoActiveFor?: readonly string[];
  /** One of the phone's tabs; everything else opens from More. */
  phoneTab?: boolean;
  /** "main" items lead the sidebar; "putAway" ones sit below in a small muted group. */
  group: "main" | "putAway";
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: "/feed",
    label: "Feed",
    icon: Layers,
    binding: "g f",
    alsoActiveFor: ["/focus", "/learning"],
    phoneTab: true,
    group: "main",
  },
  {
    to: "/inbox",
    label: "Inbox",
    icon: Inbox,
    binding: "g i",
    alsoActiveFor: ["/t/"],
    phoneTab: true,
    group: "main",
  },
  {
    to: "/tasks",
    label: "Tasks",
    icon: ListTodo,
    binding: "g t",
    alsoActiveFor: ["/brief"],
    phoneTab: true,
    group: "main",
  },
  { to: "/saved", label: "Saved mail", icon: Bookmark, binding: "g v", group: "main" },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    binding: "g s",
    alsoActiveFor: ["/accounts"],
    group: "main",
  },
  { to: "/hidden", label: "Set aside", icon: EyeOff, binding: "g h", group: "putAway" },
  {
    to: "/recent",
    label: "Recently done",
    icon: History,
    binding: "g r",
    alsoActiveFor: ["/fresh-start"],
    group: "putAway",
  },
  { to: "/screener", label: "New senders", icon: UserCheck, binding: "g c", group: "putAway" },
];

export const SIDEBAR_MAIN = NAV_ITEMS.filter((item) => item.group === "main");
export const SIDEBAR_PUT_AWAY = NAV_ITEMS.filter((item) => item.group === "putAway");
export const PUT_AWAY_LABEL = "Put away";

/** Accounts: reached from Settings (and G then A) on a wide screen, from More on a phone. */
export const ACCOUNTS_ITEM: NavItem = {
  to: "/accounts",
  label: "Accounts",
  icon: Mails,
  binding: "g a",
  group: "main",
};

export interface ClaudeMenuLink {
  to: "/work" | "/learning" | "/feed/config" | "/answers" | "/ask" | "/brief" | "/compose";
  label: string;
  icon: Icon;
  alsoActiveFor?: readonly string[];
}

/**
 * The Claude menu in the top bar: what Claude is doing, what it learned, how it works, and the
 * screens a wide screen otherwise reaches by key (Drafts is G then D, the brief G then B). A phone
 * has no top-bar menu, so More lists them too.
 */
export const CLAUDE_MENU_LINKS: readonly ClaudeMenuLink[] = [
  { to: "/work", label: "Under way", icon: ListChecks, alsoActiveFor: ["/audit"] },
  { to: "/learning", label: "Learning", icon: Sparkles },
  { to: "/feed/config", label: "How Claude works", icon: SlidersHorizontal },
  { to: "/answers", label: "Saved answers", icon: BookOpen },
  { to: "/ask", label: "Ask about your mail", icon: MessageCircleQuestion },
  { to: "/brief", label: "Latest brief", icon: Newspaper },
  { to: "/compose", label: "Drafts", icon: FilePen },
];

export const PHONE_TABS = NAV_ITEMS.filter((item) => item.phoneTab);
/** Under More on a phone: the main items that are not tabs, then Accounts. */
export const PHONE_MORE: readonly NavItem[] = [
  ...SIDEBAR_MAIN.filter((item) => !item.phoneTab),
  ACCOUNTS_ITEM,
];
/** Under More on a phone, below the main items, with its own small heading. */
export const PHONE_MORE_PUT_AWAY = SIDEBAR_PUT_AWAY.filter((item) => !item.phoneTab);

export function isNavActive(
  pathname: string,
  item: { to: string; alsoActiveFor?: readonly string[] | undefined },
): boolean {
  return (
    pathname === item.to ||
    pathname.startsWith(`${item.to}/`) ||
    (item.alsoActiveFor ?? []).some((p) => pathname.startsWith(p))
  );
}

/** The Claude menu item for `pathname`, if any (the menu button shows it is "open" there). */
export function activeClaudeLink(pathname: string): ClaudeMenuLink | null {
  return CLAUDE_MENU_LINKS.find((link) => isNavActive(pathname, link)) ?? null;
}

/** Screens that show the Dock (the feed and everything that configures or explains it). */
const DOCK_PATHS = ["/feed", "/learning", "/work", "/hidden", "/audit"];

function under(pathname: string, paths: readonly string[]): boolean {
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export interface ShellChrome {
  sidebar: boolean;
  topBar: boolean;
  briefBanner: boolean;
  dock: boolean;
  bottomNav: boolean;
}

/**
 * The chrome around a screen. Focus mode (/focus) hides all of it (Esc leaves focus), and so does
 * the guided first run (/welcome), which has its own calm frame.
 */
export function shellChrome(pathname: string): ShellChrome {
  const focus = under(pathname, ["/focus", "/welcome"]);
  return {
    sidebar: !focus,
    topBar: !focus,
    briefBanner: !focus,
    dock: !focus && under(pathname, DOCK_PATHS),
    bottomNav: !focus,
  };
}
